(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-md-quote]';
  const SEARCH_DELAY = 280;
  const MIN_QUERY_LENGTH = 2;
  const t = (key, fallback = '', replacements = {}) => window.MDT ? window.MDT(key, fallback, replacements) : fallback;

  function debounce(callback, delay) {
    let timeoutId;

    return (...args) => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => callback(...args), delay);
    };
  }

  function shopRoot() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  function productHandle(product) {
    if (product?.handle) return String(product.handle);

    try {
      const pathname = new URL(product?.url || '', window.location.origin).pathname;
      const match = pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (error) {
      return '';
    }
  }

  function productImage(product) {
    const featuredImage = product?.featured_image;

    if (typeof featuredImage === 'string') return featuredImage;
    if (featuredImage?.url) return featuredImage.url;
    if (featuredImage?.src) return featuredImage.src;
    if (product?.image) return product.image;

    return '';
  }

  function normalizeImageUrl(url, width = 180) {
    if (!url) return '';

    try {
      const imageUrl = new URL(url, window.location.origin);
      imageUrl.searchParams.set('width', String(width));
      return imageUrl.toString();
    } catch (error) {
      return url;
    }
  }

  function createImage(url, alt, className) {
    if (!url) {
      const placeholder = document.createElement('span');
      placeholder.className = className;
      placeholder.setAttribute('aria-hidden', 'true');
      return placeholder;
    }

    const image = document.createElement('img');
    image.src = normalizeImageUrl(url);
    image.alt = alt || '';
    image.width = 180;
    image.height = 180;
    image.loading = 'lazy';
    return image;
  }

  function serializeProduct(product) {
    const parts = [product.title];

    if (product.variant && product.variant !== 'Default Title') {
      parts.push(`variant: ${product.variant}`);
    }
    if (product.sku) parts.push(`SKU: ${product.sku}`);
    if (product.quantity) parts.push(`${t('quote.quantityLabel', 'Desired quantity')}: ${product.quantity}`);
    if (product.url) parts.push(product.url);

    return parts.filter(Boolean).join(' — ');
  }

  function categoryFromQuery(value) {
    const normalized = String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[_\s]+/g, '-')
      .replace(/[^a-z0-9à-ÿ-]/g, '');

    // Prefer stable category codes in URLs, but keep backwards-compatible aliases.
    if (/^(solar|solar-panels?|zonnepanelen?)$/.test(normalized) || /panel|zonne|solar/.test(normalized)) {
      return 'solar';
    }
    if (/^(inverters?|omvormers?)$/.test(normalized) || /omvorm|inverter/.test(normalized)) {
      return 'inverters';
    }
    if (
      /^(batteries|battery|home-batteries|home-battery|thuisbatterijen?|batterien?)$/.test(normalized) ||
      /batter|accu|storage/.test(normalized)
    ) {
      return 'batteries';
    }
    if (
      /^(parts?|components?|komponenten|composants|onderdelen?)$/.test(normalized) ||
      /onderdeel|component|komponent|composant|montage|mount|rail/.test(normalized)
    ) {
      return 'parts';
    }

    return '';
  }

  function initQuote(root) {
    if (!root || root.dataset.mdQuoteInitialized === 'true') return;
    root.dataset.mdQuoteInitialized = 'true';

    const form = root.querySelector('.md-quote__form-element');
    const success = root.querySelector('[data-md-quote-success]');
    const errors = root.querySelector('[data-md-quote-errors]');

    if (success) {
      success.focus({ preventScroll: true });
      success.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else if (errors) {
      errors.focus({ preventScroll: true });
      errors.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    if (!form) return;

    const search = root.querySelector('[data-md-product-search]');
    const searchInput = root.querySelector('[data-md-product-search-input]');
    const results = root.querySelector('[data-md-product-search-results]');
    const status = root.querySelector('[data-md-product-search-status]');
    const spinner = root.querySelector('[data-md-product-search-spinner]');
    const selectedList = root.querySelector('[data-md-selected-products]');
    const selectedInput = root.querySelector('[data-md-selected-products-input]');
    const productQueryInput = root.querySelector('[data-md-product-query-input]');
    const quantityField = root.querySelector('[data-md-general-quantity]');
    const quantityInput = root.querySelector('[data-md-general-quantity-input]');
    const categoryInputs = Array.from(root.querySelectorAll('[data-md-category-input]'));
    const categoryValidation = root.querySelector('[data-md-category-validation]');
    const selectedProducts = [];
    const queryParameters = new URLSearchParams(window.location.search);
    const requestedQuantity = queryParameters.get('quantity') || '';
    let searchController = null;
    let activeResultIndex = -1;

    function setSearchExpanded(expanded) {
      if (!searchInput || !results) return;
      searchInput.setAttribute('aria-expanded', String(expanded));
      results.hidden = !expanded;
      if (!expanded) activeResultIndex = -1;
    }

    function setLoading(isLoading) {
      if (spinner) spinner.hidden = !isLoading;
    }

    function updateHiddenProducts() {
      if (selectedInput) {
        selectedInput.value = selectedProducts.map(serializeProduct).join('\n');
      }

      if (selectedList) {
        selectedList.hidden = selectedProducts.length === 0;
      }
    }

    function updateQuantityMode() {
      const hasSelectedProducts = selectedProducts.length > 0;

      if (quantityField) quantityField.hidden = hasSelectedProducts;
      if (quantityInput) {
        quantityInput.disabled = hasSelectedProducts;
        quantityInput.required = !hasSelectedProducts;
      }
    }

    function renderSelectedProducts() {
      if (!selectedList) return;
      selectedList.replaceChildren();

      selectedProducts.forEach((product, index) => {
        const item = document.createElement('div');
        item.className = 'md-quote__selected-product';

        item.appendChild(
          createImage(product.image, product.title, 'md-quote__selected-product-image')
        );

        const copy = document.createElement('span');
        copy.className = 'md-quote__selected-product-copy';
        const title = document.createElement('strong');
        const meta = document.createElement('small');
        title.textContent = product.title;

        const metaParts = [];
        if (product.vendor) metaParts.push(product.vendor);
        if (product.variant && product.variant !== 'Default Title') metaParts.push(product.variant);
        if (product.sku) metaParts.push(`SKU ${product.sku}`);
        meta.textContent = metaParts.join(' · ') || t('quote.productAdded', 'Product added');
        copy.append(title, meta);
        item.appendChild(copy);

        const quantity = document.createElement('label');
        quantity.className = 'md-quote__selected-product-quantity';

        const quantityLabel = document.createElement('span');
        quantityLabel.innerHTML = `${t('quote.quantityLabel', 'Desired quantity')} <b>*</b>`;

        const quantityInputElement = document.createElement('input');
        quantityInputElement.type = 'text';
        quantityInputElement.required = true;
        quantityInputElement.placeholder = t('quote.quantityPlaceholder', 'For example 10 units');
        quantityInputElement.value = product.quantity || '';
        quantityInputElement.dataset.index = String(index);
        quantityInputElement.setAttribute('data-md-product-quantity', '');
        quantityInputElement.setAttribute('aria-label', t('quote.quantityFor', 'Desired quantity for {title}', { title: product.title }));

        quantity.append(quantityLabel, quantityInputElement);
        item.appendChild(quantity);

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'md-quote__remove-product';
        remove.dataset.removeIndex = String(index);
        remove.setAttribute('aria-label', t('quote.removeProduct', 'Remove {title}', { title: product.title }));
        remove.textContent = '×';
        item.appendChild(remove);

        selectedList.appendChild(item);
      });

      updateQuantityMode();
      updateHiddenProducts();
    }

    function addProduct(product) {
      if (!product?.title) return;

      const identifier = String(product.handle || product.url || product.title).toLowerCase();
      const alreadySelected = selectedProducts.some((selected) => {
        const selectedIdentifier = String(
          selected.handle || selected.url || selected.title
        ).toLowerCase();
        return selectedIdentifier === identifier;
      });

      if (alreadySelected) {
        if (status) status.textContent = t('quote.alreadyAddedTitle', '{title} is already added.', { title: product.title });
        return;
      }

      selectedProducts.push({
        handle: product.handle || '',
        title: product.title,
        vendor: product.vendor || '',
        url: product.url || '',
        image: product.image || '',
        variant: product.variant || '',
        sku: product.sku || '',
        quantity: product.quantity || ''
      });

      renderSelectedProducts();

      if (searchInput) {
        searchInput.value = '';
        searchInput.focus();
      }
      if (productQueryInput) productQueryInput.value = '';
      if (status) status.textContent = t('quote.addedTitle', '{title} added.', { title: product.title });
      setSearchExpanded(false);
    }

    function createResultButton(product, index) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'md-quote__search-result';
      button.setAttribute('role', 'option');
      button.setAttribute('aria-selected', 'false');
      button.dataset.index = String(index);

      const imageUrl = productImage(product);
      button.appendChild(
        createImage(imageUrl, product.title, 'md-quote__search-result-placeholder')
      );

      const copy = document.createElement('span');
      const title = document.createElement('strong');
      const vendor = document.createElement('small');
      title.textContent = product.title || 'Product';
      vendor.textContent = product.vendor || 'MD Energeez';
      copy.append(title, vendor);
      button.appendChild(copy);

      const add = document.createElement('span');
      add.textContent = '+';
      add.setAttribute('aria-hidden', 'true');
      button.appendChild(add);

      button.addEventListener('click', () => {
        addProduct({
          handle: productHandle(product),
          title: product.title || t('quote.productFallback', 'Product'),
          vendor: product.vendor || '',
          url: product.url ? new URL(product.url, window.location.origin).href : '',
          image: imageUrl
        });
      });

      return button;
    }

    function renderResults(products, query) {
      if (!results) return;
      results.replaceChildren();

      if (!products.length) {
        const empty = document.createElement('p');
        empty.className = 'md-quote__search-empty';
        empty.textContent = t('quote.noProductsQuery', 'No products found for “{query}”.', { query });
        empty.style.margin = '0';
        empty.style.padding = '14px';
        empty.style.color = '#71819a';
        empty.style.fontSize = '11px';
        results.appendChild(empty);
        setSearchExpanded(true);
        if (status) status.textContent = t('quote.noProducts', 'No products found.');
        return;
      }

      products.forEach((product, index) => {
        results.appendChild(createResultButton(product, index));
      });

      setSearchExpanded(true);
      if (status) {
        status.textContent = t('quote.productsFound', '{count} product(s) found.', { count: products.length });
      }
    }

    async function searchProducts(query) {
      const normalizedQuery = query.trim();

      if (productQueryInput) productQueryInput.value = normalizedQuery;

      if (normalizedQuery.length < MIN_QUERY_LENGTH) {
        if (searchController) searchController.abort();
        setLoading(false);
        setSearchExpanded(false);
        if (status) status.textContent = '';
        return;
      }

      if (searchController) searchController.abort();
      searchController = new AbortController();
      setLoading(true);
      if (status) status.textContent = t('quote.searching', 'Searching products…');

      const parameters = new URLSearchParams({
        q: normalizedQuery,
        'resources[type]': 'product',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last'
      });

      try {
        const response = await fetch(
          `${shopRoot()}search/suggest.json?${parameters.toString()}`,
          {
            headers: { Accept: 'application/json' },
            signal: searchController.signal
          }
        );

        if (!response.ok) throw new Error(`Search failed: ${response.status}`);

        const payload = await response.json();
        const products = payload?.resources?.results?.products || [];
        renderResults(products, normalizedQuery);
      } catch (error) {
        if (error.name === 'AbortError') return;
        setSearchExpanded(false);
        if (status) status.textContent = t('quote.searchError', 'Search is currently unavailable.');
      } finally {
        setLoading(false);
      }
    }

    const debouncedSearch = debounce(searchProducts, SEARCH_DELAY);

    if (searchInput) {
      searchInput.addEventListener('input', (event) => {
        debouncedSearch(event.target.value);
      });

      searchInput.addEventListener('keydown', (event) => {
        if (!results || results.hidden) return;
        const resultButtons = Array.from(
          results.querySelectorAll('.md-quote__search-result')
        );
        if (!resultButtons.length) return;

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          const direction = event.key === 'ArrowDown' ? 1 : -1;
          activeResultIndex =
            (activeResultIndex + direction + resultButtons.length) % resultButtons.length;

          resultButtons.forEach((button, index) => {
            const active = index === activeResultIndex;
            button.classList.toggle('is-active', active);
            button.setAttribute('aria-selected', String(active));
          });
          resultButtons[activeResultIndex].scrollIntoView({ block: 'nearest' });
        } else if (event.key === 'Enter' && activeResultIndex >= 0) {
          event.preventDefault();
          resultButtons[activeResultIndex].click();
        } else if (event.key === 'Escape') {
          setSearchExpanded(false);
        }
      });
    }

    if (selectedList) {
      selectedList.addEventListener('input', (event) => {
        const input = event.target.closest('[data-md-product-quantity]');
        if (!input) return;

        const index = Number(input.dataset.index);
        if (!Number.isInteger(index) || !selectedProducts[index]) return;

        selectedProducts[index].quantity = input.value.trim();
        updateHiddenProducts();
      });

      selectedList.addEventListener('click', (event) => {
        const button = event.target.closest('[data-remove-index]');
        if (!button) return;

        const index = Number(button.dataset.removeIndex);
        if (!Number.isInteger(index) || !selectedProducts[index]) return;

        const removedTitle = selectedProducts[index].title;
        selectedProducts.splice(index, 1);
        renderSelectedProducts();
        if (status) status.textContent = t('quote.removedTitle', '{title} removed.', { title: removedTitle });
      });
    }

    document.addEventListener('click', (event) => {
      if (search && !search.contains(event.target)) setSearchExpanded(false);
    });

    function updateCategories(showError = false) {
      const selectedCategories = categoryInputs
        .filter((input) => input.checked)
        .map((input) => input.value);

      if (categoryValidation) {
        categoryValidation.value = selectedCategories.join(', ');
        categoryValidation.setCustomValidity(
          selectedCategories.length ? '' : t('quote.categoryRequired', 'Select at least one product category.')
        );
      }

      const categories = categoryInputs[0]?.closest('.md-quote__categories');
      categories?.classList.toggle(
        'has-error',
        showError && selectedCategories.length === 0
      );
      return selectedCategories;
    }

    categoryInputs.forEach((input) => {
      input.addEventListener('change', () => {
        updateCategories(true);
      });
    });

    if (categoryValidation) {
      categoryValidation.setCustomValidity(t('quote.categoryRequired', 'Select at least one product category.'));
      categoryValidation.addEventListener('invalid', () => {
        categoryInputs[0]
          ?.closest('.md-quote__categories')
          ?.classList.add('has-error');
      });
    }

    form.addEventListener('submit', () => {
      if (productQueryInput && searchInput) {
        productQueryInput.value = searchInput.value.trim();
      }
      updateHiddenProducts();
      updateCategories(true);
    });

    async function prefillFromProduct(handle, variantId) {
      if (!handle) return;

      try {
        const response = await fetch(
          `${shopRoot()}products/${encodeURIComponent(handle)}.js`,
          { headers: { Accept: 'application/json' } }
        );
        if (!response.ok) throw new Error(`Product failed: ${response.status}`);

        const product = await response.json();
        const variants = Array.isArray(product.variants) ? product.variants : [];
        const selectedVariant =
          variants.find((variant) => String(variant.id) === String(variantId)) ||
          variants.find((variant) => variant.available) ||
          variants[0];

        addProduct({
          handle,
          title: product.title || handle,
          vendor: product.vendor || '',
          url: new URL(`${shopRoot()}products/${handle}`, window.location.origin).href,
          image: product.featured_image || product.images?.[0] || '',
          variant: selectedVariant?.title || '',
          sku: selectedVariant?.sku || '',
          quantity: requestedQuantity
        });
      } catch (error) {
        const title = new URLSearchParams(window.location.search).get('product_title');
        if (title) addProduct({ handle, title });
      }
    }

    const requestedCategories = String(queryParameters.get('category') || '')
      .split(',')
      .map(categoryFromQuery)
      .filter(Boolean);
    categoryInputs.forEach((input) => {
      if (requestedCategories.includes(input.dataset.mdCategoryCode || '')) input.checked = true;
    });
    updateCategories();

    if (quantityInput && requestedQuantity) quantityInput.value = requestedQuantity;

    const handle = queryParameters.get('product_handle');
    const variantId = queryParameters.get('variant_id');
    const productTitle = queryParameters.get('product_title');

    if (handle) {
      prefillFromProduct(handle, variantId);
    } else if (productTitle) {
      addProduct({
        title: productTitle,
        variant: queryParameters.get('variant') || '',
        sku: queryParameters.get('sku') || '',
        url: queryParameters.get('product_url') || '',
        image: queryParameters.get('image') || '',
        quantity: requestedQuantity
      });
    }

    updateQuantityMode();
  }

  function initAll(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) initQuote(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initQuote);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
